import * as React from 'react';
import { Checkbox } from '@material-ui/core';
import { MomentConfig } from '../../components/DataDisplay/MomentConfig';
import { DateTimeDisplayStyle } from '../../components/DataDisplay/DateTimeDisplayStyle';
import { FormattedDateTime } from '../../components/DataDisplay/FormattedDateTime';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { MatterTerminology } from '../../MatterTerminology';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import glamorous from 'glamorous';
import { FormattedCurrency } from '../../components/DataDisplay/FormattedCurrency';
import { CurrencyDisplayStyle } from '../../components/DataDisplay/CurrencyDisplayStyle';
import { MatterOptions, MATTER_DETAILS_REFRESH_EVENT } from '../MatterDetails';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';
import { BorderedInfoTable, BorderedInfoTableField } from '../../components/DataDisplay/BorderedInfoTable';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import EventSystem from '../../EventSystem';
// import { RvStringDisplayX } from '../../components/DataDisplay/StringDisplayX';

// tslint:disable-next-line:no-any
const ImageContainer = glamorous.div<{theme?: any}>((props) => ({
    maxWidth: '150px',
    [`& ${`img`}`]: {
        width: '100%',
        height: 'auto',
    }
}));

const ImageTableSpacer = glamorous.div({
    marginTop: '20px',
});

// Read-only "Awaiting Instructions" indicator. MUI's default 9px padding would make this one
// row taller than every other row in the table, so it's zeroed (like the Semantic checkbox
// it replaces, which had no padding).
const ReadOnlyCheckbox = glamorous(Checkbox)({
    padding: '0 !important',
});

interface ExternalProps {
    id: string;
}

interface ImageData {
    recordCount: number;
    // tslint:disable-next-line:no-any
    image: any[];
}

interface MatterGeneralData {
    id: string;
    fileNumber: string;
    title: string;
    status: string;
    fileLocation: string;
    matterType: string;
    matterCategory: string;
    state: string;
    country: string;
    family: string;
    property: string;
    propertyLabel: string;
    stage: string;
    firbStatus: boolean;
    description: string;
    lastUpdate: Date;
    images: ImageData;
    isAwaitingInstructions: boolean | undefined;
    notes: string;
    financials: {
        fundsRecovered: number;
    };
}

interface Data {
    loading?: boolean;
    networkStatus?: number;
    matter?: MatterGeneralData;
    refetch: () => void;
}

interface MatterGeneralInfoProps extends ExternalProps {
    data: Data;
    options: MatterOptions;
}

// tslint:disable-next-line:no-any
class RvMatterGeneralInfo extends React.Component<MatterGeneralInfoProps, {}>  {

    componentDidMount() {
        EventSystem.on(MATTER_DETAILS_REFRESH_EVENT, this.onRefresh);
    }

    componentWillUnmount() {
        EventSystem.event.removeListener(MATTER_DETAILS_REFRESH_EVENT, this.onRefresh);
    }

    private onRefresh = () => {
        this.props.data.refetch();
    }

    render() {
        if (this.props.data.loading) {
            return <SkeletonLoader variant="panel" columns={2} rows={8} />;
        }

        var generalInfoFields = new Array<BorderedInfoTableField>();
        var imageFields = new Array<BorderedInfoTableField>();

        if (this.props.data.matter) {
            var matterDetails = this.props.data.matter;
            var matterOptions = this.props.options;

            if (matterDetails.fileNumber) {
                generalInfoFields.push({ label: MatterTerminology.FileNumber, value: matterDetails.fileNumber });
            }
            if (matterDetails.title) {
                generalInfoFields.push({ label: MatterTerminology.Title, value: matterDetails.title });
            }
            if (matterDetails.status) {
                generalInfoFields.push({ label: MatterTerminology.Status, value: matterDetails.status });
            }
            if (matterDetails.matterCategory) {
                generalInfoFields.push({ label: MatterTerminology.Category, value: matterDetails.matterCategory });
            }
            if (matterDetails.matterType) {
                generalInfoFields.push({ label: MatterTerminology.MatterType, value: matterDetails.matterType });
            }
            if (matterDetails.fileLocation) {
                generalInfoFields.push({ label: MatterTerminology.FileLocation, value: matterDetails.fileLocation });
            }
            if (matterDetails.state) {
                generalInfoFields.push({ label: MatterTerminology.State, value: matterDetails.state });
            }
            if (matterDetails.country) {
                generalInfoFields.push({ label: MatterTerminology.Country, value: matterDetails.country });
            }
            if (matterDetails.property) {
                generalInfoFields.push({ label: MatterTerminology.Property, value: matterDetails.property });
            }
            if (matterDetails.propertyLabel) {
                generalInfoFields.push({ label: MatterTerminology.PropertyLabel, value: matterDetails.propertyLabel });
            }
            if (matterDetails.family) {
                generalInfoFields.push({ label: MatterTerminology.Family, value: matterDetails.family });
            }
            if (matterDetails.stage) {
                generalInfoFields.push({ label: MatterTerminology.Stage, value: matterDetails.stage });
            }
            if (matterDetails.lastUpdate) {
                generalInfoFields.push({
                    label: MatterTerminology.LastUpdate,
                    value: (
                        <FormattedDateTime
                            value={matterDetails.lastUpdate}
                            displayStyle={DateTimeDisplayStyle.ShortDateTime}
                            config={MomentConfig.ShowDateWithMoment}
                            DisplayInUTC={false}
                        />
                    ),
                });
            }

            if (matterDetails.isAwaitingInstructions) {
                generalInfoFields.push({
                    label: MatterTerminology.AwaitingInstructions,
                    value: <ReadOnlyCheckbox checked={matterDetails.isAwaitingInstructions} disabled={true} color="primary" size="small" />,
                });
            }

            if (matterOptions.canShowClientFinancials && UserFeature.HasAccess(UserFeatureEnum.hasMatterDebtCollectionDetails)) {
                generalInfoFields.push({
                    label: MatterTerminology.OriginalDebt,
                    value: <FormattedCurrency value={0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
                generalInfoFields.push({
                    label: MatterTerminology.CollectionCosts,
                    value: <FormattedCurrency value={0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
                generalInfoFields.push({
                    label: MatterTerminology.LegalCostsRecoverable,
                    value: <FormattedCurrency value={0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
                generalInfoFields.push({
                    label: MatterTerminology.LegalCostsNonRecoverable,
                    value: <FormattedCurrency value={0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
                generalInfoFields.push({
                    label: MatterTerminology.TotalDisbursements,
                    value: <FormattedCurrency value={0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
                generalInfoFields.push({
                    label: MatterTerminology.TotalInterest,
                    value: <FormattedCurrency value={0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
                generalInfoFields.push({
                    label: MatterTerminology.TotalPayments,
                    value: <FormattedCurrency value={0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
                generalInfoFields.push({
                    label: MatterTerminology.TotalOutstanding,
                    value: <FormattedCurrency value={0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
            }

            if (matterDetails.notes) {
                generalInfoFields.push({ label: MatterTerminology.MatterNotes, value: matterDetails.notes });
            }

            if (matterDetails.financials && matterDetails.financials.fundsRecovered) {
                generalInfoFields.push({
                    label: MatterTerminology.FundsRecovered,
                    value: <FormattedCurrency value={matterDetails.financials.fundsRecovered || 0} displayStyle={CurrencyDisplayStyle.Decimal} />,
                });
            }

            if (matterDetails.images) {
                let img = matterDetails.images.image;
                // tslint:disable-next-line:no-any
                img.forEach((value: any, index: number) => {
                    imageFields.push({
                        label: value.imageType ? value.imageType : 'Device',
                        value: (
                            <ImageContainer>
                                <img src={value.location} alt={`logos-${index}`} />
                            </ImageContainer>
                        ),
                    });
                });
            }
        }
        return (
            // Explicit request: no per-row stagger here (would mean animating inside the
            // shared BorderedInfoTable component itself, also used by Other Information's
            // nested topic fields and ValueFormatter.tsx) - one simple fade-in for the whole
            // panel instead, consistent with the same "this thing just appeared" treatment
            // used elsewhere without touching a widely-shared component.
            <AnimatedItem noFill={true}>
                <BorderedInfoTable fields={generalInfoFields} />
                {imageFields.length > 0 && (
                    <ImageTableSpacer>
                        <BorderedInfoTable fields={imageFields} />
                    </ImageTableSpacer>
                )}
            </AnimatedItem>
        );
    }
}

const GeneralInfoData = gql`query GeneralInfoData($id: Int!){
    matter(id: $id) {
        id
        matterCategory
        country
        family
        fileLocation
        fileNumber
        firbStatus
        lastUpdate
        matterType
        property
        propertyLabel
        stage
        state
        status
        description
        title,
        isAwaitingInstructions,
        notes
        images {
            recordCount
            image {
                guidId
                imageGuid
                imageType
                creationDate
                lastUpdate
                location
            }
        }
        financials{
          fundsRecovered
        }
    }
  }`;

// tslint:disable-next-line:no-any
export const GeneralInfo = graphql<any, ExternalProps, any>
    (GeneralInfoData, {
        // Was inheriting the app's global default (cache-and-network) - always re-fetching on
        // every tab mount regardless of cache. Cached after the first load; the header's
        // Refresh button (MatterDetails.tsx) forces a re-fetch via MATTER_DETAILS_REFRESH_EVENT.
        options: ({ id }) => ({ variables: { id: id }, fetchPolicy: 'cache-first' })
    })
    (RvMatterGeneralInfo);